<template>
  <div class="modal" :class="{ 'is-active': showModal }">
    <div class="modal-background"></div>
    <div class="modal-card">
      <header class="modal-card-head p-4" style="box-shadow: none;">
        <p class="modal-card-title">
          <template v-if="store.isFrontendOnly">Groundhog Day</template>
          <template v-else>It's been a while</template>
        </p>
        <button class="delete" aria-label="close" @click="store.hideModal"></button>
      </header>
      <section class="modal-card-body p-4">
        <template v-if="store.sessionStale">
          <strong>New day detected.</strong><br>
          Your previous session is from {{ sessionAge }}. Start a new session to kick off today.
        </template>
        <template v-else-if="store.isFrontendOnly">
          In Timist demo mode, you can only work one day at a time. The last session started {{ lastSessionHours }}
          hours ago. <br>
          Please start a new session.
        </template>
        <template v-else>
          Welcome back. Your browser window is still showing a past day. <br>
          Let's start a new session to get you back to today.
        </template>
      </section>
      <footer class="modal-card-foot p-4">
        <div class="buttons">
          <button class="button is-primary" @click="startNewSession">Start new session</button>
          <!-- <button class="button">Upgrade to Timist Pro</button> -->
        </div>
      </footer>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted } from 'vue';
import { useBlocksStore } from '../stores/blocks';

const store = useBlocksStore();

const showModal = computed(() => {
  // For stale sessions due to date change
  if (store.sessionStale) {
    // Demo mode: always show prompt
    if (store.isFrontendOnly) {
      return true;
    }
    // Logged-in users: auto-renewal happens immediately
    // Modal not needed since OldActiveTimerModal handles active timers
    return false;
  }

  // For expired sessions (30-hour window logic)
  if (store.isFrontendOnly) {
    const twentyHoursAgo = new Date(Date.now() - 30 * 60 * 60 * 1000);
    return store.userSession?.expired === true || new Date(store.userSession?.initializedAt) < twentyHoursAgo;
  } else {
    const thirtyHoursAgo = new Date(Date.now() - 30 * 60 * 60 * 1000);
    return new Date(store.userSession?.initializedAt) < thirtyHoursAgo;
  }
});

const startNewSession = () => {
  store.startNewSession();
};

const lastSessionHours = computed(() => {
  if (!store.userSession?.initializedAt) return 0;
  const hours = Math.floor((Date.now() - new Date(store.userSession.initializedAt)) / (60 * 60 * 1000));
  return hours;
});

const sessionAge = computed(() => {
  if (!store.userSession?.initializedAt) return 'a past day';
  const hours = Math.floor((Date.now() - new Date(store.userSession.initializedAt)) / (60 * 60 * 1000));
  const days = Math.floor(hours / 24);

  if (days === 0) return 'earlier today';
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 30) {
    const weeks = Math.floor(days / 7);
    return weeks === 1 ? '1 week ago' : `${weeks} weeks ago`;
  }
  const months = Math.floor(days / 30);
  return months === 1 ? '1 month ago' : `${months} months ago`;
});

</script>